import axios from "axios";

function computeBaseUrl() {
  const isTelegram = typeof window !== "undefined" && !!window.Telegram?.WebApp;
  if (isTelegram) return "/telegramMiniApp";
  return process.env.REACT_APP_MINIAPP_API_BASE_URL || "/telegramMiniApp";
}

const API_BASE_URL = computeBaseUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

let authToken = null;

const handleApiError = (error) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.error || error?.message || "Request failed. Please try again.";
  const err = new Error(message);
  err.status = status;
  err.data = error?.response?.data;
  throw err;
};

export const setAuthToken = (token) => {
  authToken = token || null;
};

export const clearAuthToken = () => {
  authToken = null;
};

apiClient.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

export const sendOtp = async (email, role = "driver") => {
  try {
    const response = await apiClient.post("/auth/send-otp", { email: email.trim().toLowerCase(), role });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const verifyOtp = async (email, otp, role = "driver") => {
  try {
    const response = await apiClient.post("/auth/verify-otp", { email: email.trim().toLowerCase(), otp: String(otp).trim(), role });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

// Verify driver; auto-create not required for driver
export const verifyDriver = async (initData) => {
  try {
    const response = await apiClient.post("/driver/verifyTelegramUser", { initData });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const registerDriver = async (initData, payload) => {
  try {
    const response = await apiClient.post("/createDriver", { initData, ...payload, acceptPrivacy: true });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const deleteDriverAccount = async (initData) => {
  try {
    const response = await apiClient.delete("/deleteDriver", { data: { initData } });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const updateDriverProfile = async (initData, payload) => {
  try {
    const response = await apiClient.post("/driver/profile/update", { initData, ...payload });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getDriverRides = async (telegramId) => {
  try {
    const response = await apiClient.get(`/driver/${encodeURIComponent(telegramId)}/rides`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getDriverActiveRide = async (telegramId) => {
  try {
    const response = await apiClient.get(`/driver/${encodeURIComponent(telegramId)}/active-ride`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getAvailableRides = async (telegramId) => {
  try {
    const response = await apiClient.get(`/driver/${encodeURIComponent(telegramId)}/available-rides`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const setAvailability = async (initData, { location, radiusMiles, durationHours }) => {
  try {
    const response = await apiClient.post(`/driver/availability/set`, { initData, location, radiusMiles, durationHours });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const unsetAvailability = async (initData) => {
  try {
    const response = await apiClient.post(`/driver/availability/unset`, { initData });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getAvailabilityStatus = async (telegramId) => {
  try {
    const response = await apiClient.get(`/driver/${encodeURIComponent(telegramId)}/availability`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const placeBid = async (initData, { rideId, amount }) => {
  try {
    const response = await apiClient.post(`/driver/bid`, { initData, rideId, amount });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const cancelRideByDriver = async (rideId, initData) => {
  try {
    const response = await apiClient.post(`/rides/${encodeURIComponent(rideId)}/cancel-driver`, { initData });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const completeRideByDriver = async (rideId, initData) => {
  try {
    const response = await apiClient.post(`/rides/${encodeURIComponent(rideId)}/complete-driver`, { initData });
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getDriverStats = async (telegramId) => {
  try {
    const response = await apiClient.get(`/driver/${encodeURIComponent(telegramId)}/stats`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

// NEW: list driver bids
export const getDriverBids = async (telegramId) => {
  try {
    const response = await apiClient.get(`/driver/${encodeURIComponent(telegramId)}/bids`);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export default {
  verifyDriver,
  registerDriver,
  deleteDriverAccount,
  updateDriverProfile,
  getDriverRides,
  getDriverActiveRide,
  getAvailableRides,
  setAvailability,
  unsetAvailability,
  getAvailabilityStatus,
  placeBid,
  cancelRideByDriver,
  completeRideByDriver,
  getDriverStats,
  // NEW
  getDriverBids,
};
